import { Form, PasswordField, TextField } from '@components/form-components';
import useFormState from '@hooks/useFormState';
import useTrackFormChanges from '@hooks/useTrackFormChanges';
import { Alert } from '@components/Alert';
import { Button, Link, Typography } from '@mui/material';
import { useActivateAccountMutation } from '@reactQuery/auth';
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { FormData, FormErrors } from './interfaces';
import { atLeast, isEmpty } from '@helpers/formHelpers';
import { IActivateAccountParams } from '@api/authApi/interfaces';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { WindowPageTitle } from '@components/WindowPageTitle';
import { PanelTitle } from '../components/PanelTitle';

export const ActivateAccount: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges(formData);

  const { mutate: activateAccount, isPending: isActivatingAccount } =
    useActivateAccountMutation();

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.password || isEmpty(formData.password)) {
      errors.password = t('errors.required');
    } else if (!atLeast(formData.password, 5)) {
      errors.password = t('errors.atLeast', {
        field: t('forms:login.labels.password').toLocaleLowerCase(),
        min: 5,
      });
    }

    if (!formData.retypePassword || isEmpty(formData.retypePassword)) {
      errors.retypePassword = t('errors.required');
    } else if (formData.retypePassword !== formData.password) {
      errors.retypePassword = t(
        'forms:fields.passwordField.errors.passwordsDoNotMatch'
      );
    }

    setFormErrors(errors);
  };

  const handleSubmit = () => {
    if (!Object.keys(formErrors).length) {
      const params = {
        token,
        password: formData.password,
      };

      activateAccount(params as IActivateAccountParams, {
        onError: (error) => {
          const axiosError = error as AxiosError;

          if (axiosError.response?.status === 404) {
            enqueueSnackbar(t('forms:activateAccount.messages.tokenNotValid'), {
              variant: 'error',
            });

            navigate('/login');
          } else {
            enqueueSnackbar(t('forms:activateAccount.messages.error'), {
              variant: 'error',
            });
          }
        },
        onSuccess: () => {
          enqueueSnackbar(t('forms:activateAccount.messages.success'), {
            variant: 'success',
          });

          navigate('/login');
        },
      });
    }
  };

  useEffect(() => {
    if (token === null) {
      navigate('/login');
    }
  }, [token, navigate]);

  return (
    <Fragment>
      <WindowPageTitle title={t('forms:activateAccount.title')} />
      <PanelTitle title={t('forms:activateAccount.title')} />

      <Form onSubmit={handleSubmit}>
        <Alert severity="info" content={t('forms:activateAccount.info')} />
        <PasswordField
          label={t('forms:activateAccount.labels.password')}
          placeholder={t('forms:activateAccount.placeholders.retypePassword')}
          error={formErrors.password}
          value={formData.password}
          onChange={(value) => setFormFieldData('password', value)}
          strengthMeter
        />
        <PasswordField
          label={t('forms:activateAccount.labels.retypePassword')}
          placeholder={t('forms:activateAccount.placeholders.retypePassword')}
          error={formErrors.retypePassword}
          value={formData.retypePassword}
          onChange={(value) => setFormFieldData('retypePassword', value)}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          onClick={validateForm}
          disabled={isActivatingAccount}
        >
          {t('forms:activateAccount.buttons.activate')}
        </Button>
        <Link
          component="button"
          type="button"
          onClick={() => {
            navigate('/login');
          }}
          variant="body2"
          sx={{ alignSelf: 'center' }}
        >
          {t('forms:activateAccount.buttons.goToLogin')}
        </Link>
      </Form>
    </Fragment>
  );
};
